import { ReactNode, KeyboardEvent, useState, useId } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { wacToken } from "@/libraries/design/foundations/index.ts";

import { TogglerIcon } from "./toggler-icon.tsx";

export interface AccordionProps {
    id?: string;
    header: string;
    content: ReactNode | JSX.Element;
    eventDataName?: string;
}

export const Accordion = (props: AccordionProps) => {
    const {
        id: providedId,
        header,
        content,
        eventDataName = "accordion"
    } = props;

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const fallbackId = useId();
    const id = providedId ?? fallbackId;

    const [isExpanded, setIsExpanded] = useState(false);

    const contentId = `accordion-content-${id}`;
    const headerId = `accordion-header-${id}`;

    const toggleExpansion = () => {
        if (eventDataName) {
            const data = {
                name: eventDataName
            };
            createAnalyticsEvent({
                eventName: "component clicked",
                eventType: "ui",
                event: "clicked",
                actionSubject: "component",
                action: "clicked",
                attributes: data,
                data
            }).fire();
        }
        setIsExpanded((prev) => !prev);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault(); // prevent scrolling on Space
            toggleExpansion();
        } else if (event.key === "Escape" && isExpanded) {
            setIsExpanded(false);
        }
    };

    return (
        <details css={containerCss}>
            <summary
                tabIndex={0}
                aria-expanded={isExpanded}
                aria-controls={contentId}
                aria-labelledby={headerId}
                onClick={toggleExpansion}
                onKeyDown={handleKeyDown}
                css={headerContainerCss}
            >
                <h3
                    id={headerId}
                    className="accordion-item-header body-lg"
                    css={headerTextCss}
                >
                    {header}
                </h3>

                <div>
                    <TogglerIcon icon={isExpanded ? "minus" : "plus"} />
                </div>
            </summary>

            <div
                id={contentId}
                role="region"
                aria-labelledby={headerId}
                aria-hidden={!isExpanded}
                css={[
                    contentCss,
                    isExpanded ? contentExpandedCss : contentCollapsedCss
                ]}
            >
                {content}
            </div>
        </details>
    );
};

const containerCss = css({
    "borderBlockEnd": `2px solid ${wacToken("color.neutral.light300")}`,
    "&:first-child": {
        borderBlockStart: `2px solid ${wacToken("color.neutral.light300")}`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "&:hover .accordion-item-header.body-lg": {
        color: wacToken("color.extended.blue800")
    }
});

const headerContainerCss = css({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "24px"
});

const headerTextCss = css({
    "margin": 0,
    "&.accordion-item-header.body-lg": {
        fontWeight: 700,
        fontFamily: wacToken("font.charlie.display")
    }
});

const contentCss = css({
    padding: "8px 24px 24px 24px"
});

const contentExpandedCss = css({
    display: "block"
});

const contentCollapsedCss = css({
    display: "none"
});
